import { UPSELLS_ENABLED } from "@/lib/features";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Loader2, Sparkles, X } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { track } from "@/lib/posthog";
import { startProCheckout } from "@/lib/billing";
import { PROMO } from "@/lib/promo";

export type UpsellPopupType = "design_limit" | "refine_limit" | "hard_wall";

const SAVE_PCT = Math.floor((1 - Number(PROMO.price) / Number(PROMO.regularPrice)) * 100);

/** Design popup: once per user per month. Refine popup: once per user, ever. */
function seenKey(type: UpsellPopupType, userId: string): string | null {
  // Hard wall: one-time offer — shown on each blocked Generate until dismissed once.
  if (type === "hard_wall") return `huelo:upsell-popup:hardwall-dismissed:${userId}`;
  if (type === "refine_limit") return `huelo:upsell-popup:refine:${userId}`;
  const d = new Date();
  return `huelo:upsell-popup:design:${userId}:${d.getUTCFullYear()}-${d.getUTCMonth() + 1}`;
}

interface Props {
  type: UpsellPopupType;
  /** Only opens while true (user is at the relevant limit). */
  active: boolean;
  promoEligible: boolean;
  /** Changing this value re-opens the popup (e.g. each blocked Generate click). */
  trigger?: number;
}

const UpsellDialog = ({ type, active, promoEligible, trigger }: Props) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const keyRef = useRef<string | null>(null);
  const userRef = useRef<string | null>(null);
  const props = { type, placement: type === "design_limit" ? "reminder_paywall" : type === "hard_wall" ? "hard_wall" : "refine_limit", promo_eligible: promoEligible, plan: "free" };

  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    supabase.auth.getUser().then(async ({ data }) => {
      const uid = data.user?.id;
      if (!uid || cancelled) return;
      const key = seenKey(type, uid);
      if (type === "hard_wall") {
        const { data: profile, error } = await supabase.from("profiles").select("hard_wall_offer_dismissed").eq("user_id", uid).single();
        if (cancelled || error || profile?.hard_wall_offer_dismissed) return;
      }
      if (key) { try { if (localStorage.getItem(key)) return; if (type !== "hard_wall") localStorage.setItem(key, "1"); } catch { /* ignore */ } }
      keyRef.current = key;
      userRef.current = uid;
      setOpen(true);
      track("upsell_popup_viewed", props);
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, type, trigger]);

  const close = () => {
    track("upsell_popup_dismissed", props);
    if (type === "hard_wall" && keyRef.current) { try { localStorage.setItem(keyRef.current, "1"); } catch { /* ignore */ } }
    if (type === "hard_wall" && userRef.current) {
      void supabase.from("profiles").update({ hard_wall_offer_dismissed: true }).eq("user_id", userRef.current).then(({ error }) => {
        if (error) console.warn("Couldn't save offer dismissal");
      });
    }
    setOpen(false);
  };

  const claim = async () => {
    track("upsell_popup_clicked", props);
    setBusy(true);
    try {
      window.open(await startProCheckout("month", "pro", props.placement), "_blank");
      setOpen(false);
    } catch {
      navigate("/pricing?plan=pro&billing=monthly");
    } finally {
      setBusy(false);
    }
  };

  const isDesign = type === "design_limit";
  const isWall = type === "hard_wall";
  const pill = isDesign ? "Limited-time offer" : "One-time offer";

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) close(); }}>
      <DialogContent className="max-w-[420px] p-0 gap-0 overflow-hidden rounded-3xl border-0 [&>button]:hidden">
        <div className="relative bg-gradient-hero px-6 pt-7 pb-6 text-center text-primary-foreground">
          <button
            aria-label="Close"
            onClick={close}
            className="absolute top-4 right-4 w-8 h-8 rounded-full bg-primary-foreground/15 hover:bg-primary-foreground/25 flex items-center justify-center"
          >
            <X className="w-4 h-4" />
          </button>
          {promoEligible && (
            <span className="inline-flex items-center gap-1.5 rounded-full border border-primary-foreground/40 bg-primary-foreground/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-widest">
              <Sparkles className="w-3 h-3" /> {pill}
            </span>
          )}
          {isDesign ? (
            <>
              <DialogTitle className="mt-3 text-4xl font-bold leading-none">
                {promoEligible ? <>{SAVE_PCT}% <span className="text-lg">off</span></> : "Go Pro"}
              </DialogTitle>
              <p className="mt-2 text-sm font-medium text-primary-foreground/90">Your first design is just the start</p>
            </>
          ) : (
            <>
              <DialogTitle className="mt-3 text-2xl font-bold leading-tight">
                {isWall
                  ? (promoEligible ? <>Keep designing for ${PROMO.price}</> : <>Keep designing with Pro</>)
                  : (promoEligible ? <>Keep perfecting your room for ${PROMO.price}</> : <>Keep perfecting your room with Pro</>)}
              </DialogTitle>
              <p className="mt-2 text-sm text-primary-foreground/90">
                {promoEligible ? <><span className="font-semibold">{SAVE_PCT}% off</span> your first month. You won't see this offer again.</> : (isWall ? "You've used this month's free design" : "You've used this month's refinements")}
              </p>
            </>
          )}
        </div>

        <div className="px-6 pt-5 pb-5 text-center">
          {isDesign && (
            <>
              <h3 className="text-xl font-semibold text-foreground">Unlock Huelo Pro</h3>
              <DialogDescription className="mt-1 text-sm text-muted-foreground">
                You've used this month's free design. Go Pro to keep designing.
              </DialogDescription>
              <div className="mt-4 flex items-baseline justify-center gap-2 flex-wrap">
                <span className="text-3xl font-bold text-foreground">${promoEligible ? PROMO.price : PROMO.regularPrice}</span>
                <span className="text-sm text-muted-foreground">{promoEligible ? "first month" : "/month"}</span>
                {promoEligible && (
                  <>
                    <span className="text-sm text-muted-foreground line-through">${PROMO.regularPrice}</span>
                    <span className="rounded-full bg-accent/20 text-secondary px-2 py-0.5 text-[11px] font-semibold">Save {SAVE_PCT}%</span>
                  </>
                )}
              </div>
              <ul className="mt-4 space-y-3 rounded-2xl border border-border bg-muted/50 p-4 text-left">
                {[
                  ["5 new designs every month", "Try new rooms or fresh styles"],
                  ["15 refinements a month", "Swap pieces and fine-tune until it feels right"],
                ].map(([t, d]) => (
                  <li key={t} className="flex gap-3">
                    <span className="mt-0.5 shrink-0 w-5 h-5 rounded-full bg-gradient-cta flex items-center justify-center">
                      <Check className="w-3 h-3 text-primary-foreground" strokeWidth={3} />
                    </span>
                    <div>
                      <p className="text-sm font-semibold text-foreground">{t}</p>
                      <p className="text-xs text-muted-foreground">{d}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </>
          )}
          {!isDesign && <DialogDescription className="sr-only">{isWall ? "Upgrade to Pro for more designs" : "Upgrade to Pro for more refinements"}</DialogDescription>}

          <button
            onClick={claim}
            disabled={busy}
            className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full bg-gradient-cta text-primary-foreground py-3.5 text-sm font-semibold shadow-elegant hover:opacity-95 disabled:opacity-70"
          >
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}
            {isDesign ? (promoEligible ? "Claim my offer" : "Upgrade to Pro") : (promoEligible ? `Get Pro for $${PROMO.price}` : "Upgrade to Pro")}
          </button>
          <p className="mt-2 text-[11px] text-muted-foreground">
            {promoEligible ? `$${PROMO.price} first month, then $${PROMO.regularPrice}/mo · Cancel anytime` : "Cancel anytime"}
          </p>
          <div className="mt-3 flex flex-col items-center gap-1.5">
            <button onClick={close} className="text-sm font-medium text-muted-foreground hover:text-foreground">
              {isDesign ? "Maybe later" : "No thanks"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const UpsellDialogGate = (p: Props) => (UPSELLS_ENABLED ? <UpsellDialog {...p} /> : null);
export default UpsellDialogGate;
